import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import {
  Form,
  FormGroup,
  InputGroup,
  InputGroupText,
  Label,
  Spinner,
} from "reactstrap";
import { Button, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { HugeiconsIcon } from "@hugeicons/react";
import { Mail01Icon, SquareLock01Icon } from "@hugeicons/core-free-icons";
import { useAuthContext } from "../../_helper/Auth";
import { AntInput } from "../../CommonElements/AntInput";
import {
  useResendEmailVerificationMutation,
  useVerifyEmailMutation,
} from "../../services/useAuth/emailVerification";
import { MANDATORY_CHANGE_PASSWORD_PATH } from "../../Routes/PrivateRoute";

const RESEND_COOLDOWN_SEC = 60;

const getErrorMessage = (err, t) => {
  const data = err?.response?.data;
  if (typeof data?.message === "string") return data.message;
  const otpErr = data?.errors?.otp;
  if (Array.isArray(otpErr)) return otpErr[0];
  if (typeof otpErr === "string") return otpErr;
  const codeErr = data?.errors?.code;
  if (Array.isArray(codeErr)) return codeErr[0];
  if (codeErr != null) return String(codeErr);
  return t("EMAIL_VERIFICATION_FAILED");
};

export default function VerifyEmail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    token,
    emailVerificationPending,
    passwordChangeRequired,
    setEmailVerificationPending,
    addUser,
    addToken,
  } = useAuthContext();

  const [apiError, setApiError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SEC);
  const intervalRef = useRef(null);

  const verifyMutation = useVerifyEmailMutation();
  const resendMutation = useResendEmailVerificationMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { code: "" },
  });

  useEffect(() => {
    if (!token?.access_token) {
      navigate("/login", { replace: true });
      return;
    }
    if (passwordChangeRequired) {
      navigate(MANDATORY_CHANGE_PASSWORD_PATH, { replace: true });
      return;
    }
    if (!emailVerificationPending) {
      navigate("/app", { replace: true });
    }
  }, [token, emailVerificationPending, passwordChangeRequired, navigate]);

  const stopCountdown = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startCooldown = useCallback(() => {
    stopCountdown();
    setResendCooldown(RESEND_COOLDOWN_SEC);
    intervalRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [stopCountdown]);

  useEffect(() => {
    startCooldown();
    return stopCountdown;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (data) => {
    setApiError("");
    verifyMutation.mutate(
      { otp: data.code.trim() },
      {
        onSuccess: (res) => {
          const d = res?.data;
          if (d?.access_token && d?.refresh_token) {
            addToken({
              access_token: d.access_token,
              refresh_token: d.refresh_token,
              expires_in: d.expires_in,
            });
          }
          if (d?.user) {
            addUser(d.user);
          }
          setEmailVerificationPending(false);
          navigate("/app", { replace: true });
        },
        onError: (err) => {
          setApiError(getErrorMessage(err, t));
        },
      }
    );
  };

  const handleResend = () => {
    if (resendCooldown > 0 || resendMutation.isPending) return;
    setApiError("");
    resendMutation.mutate(undefined, {
      onSuccess: () => {
        startCooldown();
      },
      onError: (err) => {
        setApiError(getErrorMessage(err, t));
      },
    });
  };

  const formatCountdown = () => {
    const m = Math.floor(resendCooldown / 60);
    const s = resendCooldown % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (!token?.access_token || !emailVerificationPending) {
    return (
      <div className="login-card1 decorative-arabic-text-bg h-100 d-flex justify-content-center p-5">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="login-card1 decorative-arabic-text-bg h-100">
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="theme-form login-form pattern-card rounded-3 border border-1"
      >
        <Stack gap={3}>
          <h4>{t("EMAIL_VERIFICATION_TITLE")}</h4>
          <h6 className="text-muted fw-normal">{t("EMAIL_VERIFICATION_HINT")}</h6>
        </Stack>

        {apiError && (
          <div className="alert alert-danger-outline text-danger gap-2 border border-danger rounded-3 mb-3 d-flex align-items-center">
            <div className="me-2">
              <HugeiconsIcon
                icon={SquareLock01Icon}
                style={{ color: "#dc3545", fontSize: "18px" }}
              />
            </div>
            <div className="flex-grow-1">
              <small className="text-black h6">{apiError}</small>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => setApiError("")}
              aria-label="Close"
            />
          </div>
        )}

        <FormGroup>
          <Label>{t("EMAIL_VERIFICATION_CODE_LABEL")}</Label>
          <Controller
            name="code"
            control={control}
            rules={{
              required: t("EMAIL_VERIFICATION_CODE_REQUIRED"),
              minLength: {
                value: 4,
                message: t("EMAIL_VERIFICATION_CODE_MIN"),
              },
            }}
            render={({ field }) => (
              <InputGroup>
                <InputGroupText>
                  <HugeiconsIcon icon={Mail01Icon} />
                </InputGroupText>
                <AntInput
                  {...field}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  status={errors.code ? "error" : ""}
                  placeholder={t("EMAIL_VERIFICATION_CODE_PLACEHOLDER")}
                />
              </InputGroup>
            )}
          />
          {errors.code && (
            <div className="text-danger small mt-1">{errors.code.message}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Button
            disabled={verifyMutation.isPending}
            type="submit"
            variant="primary"
            size="lg"
            className="w-100"
          >
            <Stack
              direction="horizontal"
              gap={2}
              className="justify-content-center"
            >
              {verifyMutation.isPending && <Spinner size="sm" />}
              {t("EMAIL_VERIFICATION_SUBMIT")}
            </Stack>
          </Button>
        </FormGroup>

        <FormGroup className="text-center">
          {resendCooldown > 0 ? (
            <p className="text-muted small mb-0">
              {t("EMAIL_VERIFICATION_RESEND_IN", { time: formatCountdown() })}
            </p>
          ) : (
            <Button
              type="button"
              variant="link"
              className="p-0"
              disabled={resendMutation.isPending}
              onClick={handleResend}
            >
              {resendMutation.isPending && <Spinner size="sm" className="me-2" />}
              {t("EMAIL_VERIFICATION_RESEND")}
            </Button>
          )}
        </FormGroup>
      </Form>
    </div>
  );
}
